// src/components/chat/NewConversationPicker.jsx
//
// Small overlay listing everyone the caller is eligible to start a new
// conversation with, for one specific conversationType at a time (the
// parent decides which type by which button opened the picker — e.g.
// "Message a Doctor" vs "Message a Navigator" are two separate buttons,
// each opening this same component with a different conversationType).
//
// Used by PatientCareTeamPage.jsx, PatientMessagesInboxPage.jsx and
// SubscriberChatPage.jsx — all backed by GET
// /api/conversations/eligible-counterparts. TeamInboxPage.jsx no longer uses
// it: Team Chat lists everyone from GET /api/conversations/team-directory
// directly in its strip.

import React, { useState, useEffect } from 'react';
import { chatMessagingAPI } from '../../services/chatMessagingAPI';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function NewConversationPicker({ conversationType, title, onSelect, onClose }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(null); // id currently being started, for a per-row loading state
  // The full list already comes back in one call (no server-side query
  // param) — this is a client-side filter, not a new request per keystroke.
  // Only shown once there's enough in the list for scrolling-to-find to be
  // the actual problem — a doctor's own small navigator list, for example,
  // doesn't need it.
  const [query, setQuery] = useState('');
  const SEARCH_THRESHOLD = 6;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    chatMessagingAPI.getEligibleCounterparts(conversationType)
      .then((list) => { if (!cancelled) setOptions(list); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load the list.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [conversationType]);

  const filteredOptions = query.trim()
    ? options.filter(o => o.name?.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  async function handlePick(option) {
    setStarting(option.id);
    try {
      await onSelect(option);
    } finally {
      setStarting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(13,30,53,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(380px, 92vw)', maxHeight: '70vh', display: 'flex', flexDirection: 'column',
          background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ fontFamily: 'var(--font-display, serif)', fontSize: 16, color: 'var(--text-primary)' }}>
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            ✕
          </button>
        </div>

        {!loading && !error && options.length > SEARCH_THRESHOLD && (
          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)' }}>
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              autoFocus
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--border)', fontSize: 13,
                fontFamily: 'inherit', background: 'var(--bg-subtle)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
        )}

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
          )}
          {error && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--red)' }}>{error}</div>
          )}
          {!loading && !error && options.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              No one available to message yet.
            </div>
          )}
          {!loading && !error && options.length > 0 && filteredOptions.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
              No one matches "{query}".
            </div>
          )}

          {filteredOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handlePick(opt)}
              disabled={starting === opt.id}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 18px', border: 'none', borderBottom: '1px solid var(--border)',
                background: 'transparent', cursor: starting === opt.id ? 'not-allowed' : 'pointer',
                textAlign: 'left', fontFamily: 'inherit',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'var(--brand-accent)', color: 'var(--text-on-brand-dark)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
              }}>
                {initials(opt.name)}
              </div>
              <div style={{ flex: 1, fontSize: 13.5, color: 'var(--text-primary)' }}>
                {opt.name}
              </div>
              {starting === opt.id && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>⏳</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
